import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import titleCase from 'common/utils/titleCase';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import useViewportCap from '../hooks/useViewportCap';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
};

// fulfillStatus tracks the fulfillProductByRxNumber call made when a session
// is committed: pending → success/failed. A failed session must be re-scanned
// and committed again.
const FulfillStatus = ({ session }) => {
  if (session.fulfillStatus === 'pending') {
    return <Chip size='small' color='info' label='Recording…' />;
  }
  if (session.fulfillStatus === 'success') {
    return <Chip size='small' color='success' label='Fulfilled' />;
  }
  if (session.fulfillStatus === 'failed') {
    // fulfillError already carries the right next step for the specific
    // failure (re-scan vs. check POM first) — see fulfillErrorMessage in
    // rxScanValidation.js. No blanket suffix appended here.
    const detail =
      session.fulfillError ||
      'Failed to record fulfillment — please re-scan the session and commit it again.';
    return (
      <Tooltip title={detail}>
        <Chip size='small' color='error' label='Failed' />
      </Tooltip>
    );
  }
  // 'unknown': the page reloaded while the fulfillment call was in flight, so
  // the outcome was never observed — it may well have succeeded server-side.
  if (session.fulfillStatus === 'unknown') {
    return (
      <Tooltip title='The page was reloaded while this was being recorded — verify in POM before re-scanning.'>
        <Chip size='small' color='warning' label='Unconfirmed' />
      </Tooltip>
    );
  }
  return null;
};

FulfillStatus.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fulfillStatus: PropTypes.oneOf(['pending', 'success', 'failed', 'unknown']),
    fulfillError: PropTypes.string,
  }).isRequired,
};

// labelStatus/printStatus track the auto regenerate-and-print flow triggered
// when a committed session's dispensed NDC differs from the Rx's prescribed
// NDC, or when core is found to have retired the printed label on commit (see
// regenerateAndPrintLabel / confirmPrintedLabelStillLive in rxScanValidation.js).
// Absent entirely for sessions whose printed label survived, so this renders
// nothing for them.
const LabelPrintStatus = ({ session }) => {
  if (!session.labelStatus) return null;
  if (session.labelStatus === 'regenerating') {
    return <Chip size='small' color='info' label='Regenerating label…' />;
  }
  if (session.labelStatus === 'failed') {
    return (
      <Tooltip title='Label regeneration failed — please regenerate and print it manually.'>
        <Chip size='small' color='error' label='Label regen failed' />
      </Tooltip>
    );
  }
  // labelStatus === 'regenerated' — the label itself is done; show print progress.
  if (session.printStatus === 'printing') {
    return <Chip size='small' color='info' label='Printing…' />;
  }
  if (session.printStatus === 'printed') {
    return <Chip size='small' color='success' label='Printed' />;
  }
  if (session.printStatus === 'failed') {
    return (
      <Tooltip title='Sending the regenerated label to the printer failed — please print it manually.'>
        <Chip size='small' color='error' label='Print failed' />
      </Tooltip>
    );
  }
  // 'unknown': the page reloaded while this was in flight, so the outcome was
  // never observed — verify the label/print manually rather than assume either.
  if (session.labelStatus === 'unknown' || session.printStatus === 'unknown') {
    return (
      <Tooltip title='The page was reloaded while this was regenerating/printing — verify the label was printed correctly.'>
        <Chip size='small' color='warning' label='Unconfirmed' />
      </Tooltip>
    );
  }
  return null;
};

LabelPrintStatus.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    labelStatus: PropTypes.oneOf(['regenerating', 'regenerated', 'failed', 'unknown']),
    printStatus: PropTypes.oneOf(['printing', 'printed', 'failed', 'unknown']),
  }).isRequired,
};

const SessionHistory = ({ sessions }) => {
  const { ref: scrollRef, maxHeight } = useViewportCap();
  return (
    <Card variant='outlined' sx={{ width: '100%' }}>
      <CardContent>
        <Typography variant='h6' sx={{ mb: 1 }}>
          Committed Sessions
        </Typography>
        <Box ref={scrollRef} sx={{ maxHeight, overflow: 'auto', pr: 0.5, pb: 10 }}>
          {sessions.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No sessions committed yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {sessions.map((s) => (
                <Accordion key={s.id} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ width: '100%' }}>
                      <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
                        Rx {s.rx.rxNumber || '(no number)'} —{' '}
                        {s.rx.patientName || 'Unknown patient'}
                      </Typography>
                      <FulfillStatus session={s} />
                      <LabelPrintStatus session={s} />
                      <Chip size='small' label={`${s.vials.length} item(s)`} />
                      <Typography variant='caption' color='text.secondary'>
                        {formatTime(s.committedAt || s.startedAt)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Drug: {s.rx.drugName || '—'} · Strength: {s.rx.strength || '—'}
                      </Typography>
                    </Box>
                    {s.vials.map((v, idx) => (
                      <Box
                        key={v.id}
                        sx={{
                          p: 1,
                          mb: 0.5,
                          borderRadius: 1,
                          border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          spacing={1}
                        >
                          <Typography variant='body2'>
                            Item #{idx + 1}: {titleCase(v.fields.drugName) || '—'}{' '}
                            {titleCase(v.fields.strength) || ''}
                          </Typography>
                          {v.fields.manufacturer && (
                            <Chip size='small' variant='outlined' label={v.fields.manufacturer} />
                          )}
                        </Stack>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant='caption' color='text.secondary'>
                            Lot {v.fields.lotNumber || '—'}
                            {v.fields.recordedManually ? ` · ${SYSTEM_DEFICIT.toLowerCase()}` : ''}
                            {v.fields.recordedManually && v.fields.expirationDate
                              ? ` · BUD ${v.fields.expirationDate}`
                              : ''}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Qty {v.fields.quantity ?? '—'}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            NDC {v.fields.ndc || '—'}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

SessionHistory.propTypes = {
  sessions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
};

export default SessionHistory;
